import React, { createContext, useContext, useState, useCallback } from 'react';
import Uppy from '@uppy/core';
import UppyDashboardModalGuard from '../components/uppy/UppyDashboardModalGuard';

interface AdminUploaderProviderProps {
	children: React.ReactNode;
}

const defaultAdminUploaderContextValue: {
	uppyInstances: { [key: string]: Uppy };
	setUppyInstances: React.Dispatch<
		React.SetStateAction<{ [key: string]: Uppy }>
	>;
	openDashboard: (uppyInstance: Uppy) => void;
	closeDashboard: () => void;
} = {
	uppyInstances: {},
	setUppyInstances: () => {},
	openDashboard: () => {},
	closeDashboard: () => {},
};

const AdminUploaderContext = createContext(defaultAdminUploaderContextValue);

export const useAdminUploaderContext = () => useContext(AdminUploaderContext);

export const AdminUploaderProvider = ({
	children,
}: AdminUploaderProviderProps) => {
	const [uppyInstances, setUppyInstances] = useState<{ [key: string]: Uppy }>(
		{},
	);
	const [activeUppyInstance, setActiveUppyInstance] = useState<Uppy | null>(
		null,
	);

	const openDashboard = useCallback((uppyInstance: Uppy) => {
		setActiveUppyInstance(uppyInstance);
	}, []);

	const closeDashboard = useCallback(() => {
		setActiveUppyInstance(null);
	}, []);

	const value = {
		uppyInstances,
		setUppyInstances,
		openDashboard,
		closeDashboard,
	};

	return (
		<AdminUploaderContext.Provider value={value}>
			{children}
			{activeUppyInstance && (
				<UppyDashboardModalGuard
					uppy={activeUppyInstance}
					openDashboard={true}
					onCloseDashboard={closeDashboard}
				/>
			)}
		</AdminUploaderContext.Provider>
	);
};
